import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  faCalendar,
  faEye,
  faPlus,
  faSearch,
  faTrash,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { DateService } from 'src/app/services/date.service';
import { ToastService } from 'src/app/services/toast.service';
import { CuitService } from 'src/app/services/cuit.service';
import { CUITDataForAdd, CuitFormInitialDraft } from 'src/app/core/models/CUIT';
import { ivaCondition } from 'src/app/core/models/IvaCondition.enum';

@Component({
  selector: 'app-cuit-form',
  templateUrl: './cuit-form.component.html',
  styleUrls: ['./cuit-form.component.scss'],
})
export class CuitFormComponent implements OnInit, OnChanges {
  @Input() edit?: boolean;
  @Input() viewObject?: boolean = true;
  @Input() editData?: any;
  @Input() first: boolean;
  /** Si no es edición, precarga el formulario (p. ej. datos devueltos por AFIP). */
  @Input() initialDraft?: CuitFormInitialDraft | null;
  @Output() cancelAction = new EventEmitter();
  @Output() closeAction = new EventEmitter();


  iconCalendar = faCalendar;
  iconSearch = faSearch;
  iconPlus = faPlus;
  iconSee = faEye;
  iconTrash = faTrash;
  iconEdit = faPen;

  index: number;
  ivaTypes: any[] = [];
  receiptTypes: any[] = [];
  sellPoint: number;
  cuitToSubmit: CUITDataForAdd;
  editSp: boolean;
  submitted: boolean = false;
  minDate: Date;
  cuitBuscadorArca = '';
  buscandoCuitArca = false;



  cuitDataFormGroup = new UntypedFormGroup({
    cuitNumber: new UntypedFormControl('', Validators.required),
    ivaAccountableType: new UntypedFormControl(''),
    socialReason: new UntypedFormControl('', Validators.required),
    ibbAccountableType: new UntypedFormControl('0'),
    address: new UntypedFormControl('', Validators.required),
    sellPoints: new UntypedFormControl('', [Validators.min(0), Validators.max(2147483647)]),
    activityStartDate: new UntypedFormControl('', Validators.required),
  });


  constructor(
    public cuitService: CuitService,
    public dateService: DateService,
    private toastService: ToastService,
  ) {
    this.minDate = new Date()
    this.minDate.setDate(this.minDate.getDate()-5);
  }

  ngOnInit() {
    for (let item in ivaCondition) {
      if (isNaN(Number(item))) {
        this.ivaTypes.push({ text: item, value: ivaCondition[item] });
      }
    }

    if (this.edit) {
      this.index = this.editData.id;
      this.cuitDataFormGroup.patchValue({
        cuitNumber: this.editData.cuitNumber,
        ivaAccountableType: this.editData.ivaAccountableType,
        socialReason: this.editData.socialReason,
        address: this.editData.address,
        ibbAccountableType: this.editData.ibbAccountableType,
        sellPoints: this.editData.sellPoints,
        activityStartDate: this.dateService.transformToDateHash(
          new Date(this.editData.activityStartDate),
        ),
      });
    } else {
      this.applyInitialDraft(this.initialDraft);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialDraft'] && !this.edit) {
      this.applyInitialDraft(this.initialDraft);
    }
  }

  private applyInitialDraft(draft: CuitFormInitialDraft | null | undefined) {
    if (!draft || this.edit) {
      return;
    }
    const patch: Record<string, unknown> = {
      cuitNumber: draft.cuitNumber,
      socialReason: draft.socialReason,
      address: draft.address || '',
    };
    if (draft.activityStartDate) {
      patch.activityStartDate = draft.activityStartDate;
    }
    this.cuitDataFormGroup.patchValue(patch);
  }

  submitCuit() {
    let date = this.cuitDataFormGroup.value.activityStartDate
      ? this.cuitDataFormGroup.value.activityStartDate
      : null;

    this.cuitToSubmit = {
      cuitNumber: this.cuitDataFormGroup.value.cuitNumber
        ? this.cuitDataFormGroup.value.cuitNumber
        : null,
      ivaAccountableType: this.cuitDataFormGroup.value.ivaAccountableType
        ? this.cuitDataFormGroup.value.ivaAccountableType
        : 1,
      socialReason: this.cuitDataFormGroup.value.socialReason
        ? this.cuitDataFormGroup.value.socialReason
        : null,
      address: this.cuitDataFormGroup.value.address
        ? this.cuitDataFormGroup.value.address
        : '',
      activityStartDate: this.dateService.transformToDateObject(
        date?.day,
        date?.month,
        date?.year,
      ),
      ibbAccountableType: this.cuitDataFormGroup.value.ibbAccountableType
      ? this.cuitDataFormGroup.value.ibbAccountableType
      : '',
      sellPoints: this.cuitDataFormGroup.value.sellPoints
      ? this.cuitDataFormGroup.value.sellPoints
      : '',
    };

    if (this.cuitDataFormGroup.get('activityStartDate').hasError('ngbDate')) 
    {
      this.toastService.showToast(
        'warning',
        'Corrija el formato de la fecha a AAAA-MM-DD',
      ); 
      return;
    }
    if (this.cuitDataFormGroup.invalid) {
      this.cuitDataFormGroup.markAllAsTouched();
      this.toastService.showToast(
        'warning',
        'Hay campos obligatorios sin completar',
      );
      return;
    } else {
      if (this.edit) {
          this.editCuit(this.cuitToSubmit); 
      } else {
        this.submitted = true;
        this.addCuit(this.cuitToSubmit);
      }
    }
  }

  addCuit(cuitToSubmit) {
    this.cuitService.addCuit(cuitToSubmit).subscribe((response) => {
      this.toastService.showToast('success', 'Cuit agregado');
      this.closeAction.emit();
    },
    (err) => {
      this.toastService.showToast('error', 'Ha ocurrido un error');
      this.submitted = false;
    });
  }

  editCuit(cuitToEdit) {
    this.cuitService
      .editCuit(this.editData.id, cuitToEdit)
      .subscribe((response) => {
        this.toastService.showToast('success', 'Cuit modificado');
        this.closeAction.emit();
        
      });
  }

  validate(field: string) {
    return (
      this.cuitDataFormGroup.controls[field].errors &&
      this.cuitDataFormGroup.controls[field].touched
    );
  }

  cancel() {
    this.cancelAction.emit();
  }

  buscarEnArca() {
    const cuitLimpio = this.cuitService.limpiarCuit(this.cuitBuscadorArca);

    if (cuitLimpio.length !== 11) {
      this.toastService.showToast('warning', 'Ingrese un CUIT de 11 digitos');
      return;
    }

    this.buscandoCuitArca = true;
    this.cuitService.obtenerInfoCuit(cuitLimpio).subscribe({
      next: (info) => {
        this.buscandoCuitArca = false;
        if (!info) {
          this.toastService.showToast('warning', 'No se encontro informacion del CUIT en ARCA');
          return;
        }

        const raw = info?.raw || {};
        const nombreRaw = String(raw?.nombre || '').trim();
        const apellidoRaw = String(raw?.apellido || '').trim();
        const nombreCompletoRaw = `${nombreRaw} ${apellidoRaw}`.trim();
        const razonSocial = String(nombreCompletoRaw || info?.razonSocial || '').trim();

        const domicilios = Array.isArray(raw?.domicilio) ? raw.domicilio : [];
        const domicilioFiscal =
          domicilios.find((d) =>
            String(d?.tipoDomicilio || '')
              .toUpperCase()
              .includes('FISCAL'),
          ) || domicilios[0];
        const direccionFiscal = String(domicilioFiscal?.direccion || '').trim();
        const domicilio = String(info?.domicilio || direccionFiscal || '').trim();

        const patch: Record<string, unknown> = {
          cuitNumber: cuitLimpio,
        };
        if (razonSocial) {
          patch.socialReason = razonSocial;
        }
        if (domicilio) {
          patch.address = domicilio;
        }
        const fechaInicio = this.parseFechaInicioArca(info, raw);
        if (fechaInicio) {
          patch.activityStartDate = this.dateService.transformToDateHash(fechaInicio);
        }

        this.cuitDataFormGroup.patchValue(patch);
        this.cuitDataFormGroup.get('cuitNumber')?.markAsTouched();
        this.toastService.showToast('success', 'Datos cargados desde ARCA');
      },
      error: (err) => {
        this.buscandoCuitArca = false;
        if (err?.afipAuthFailure) {
          this.toastService.showToast(
            'warning',
            'No se pudo consultar ARCA: credenciales o servicio no disponible.',
          );
          return;
        }
        this.toastService.showToast('error', 'Error al consultar ARCA. Verifique el CUIT e intente nuevamente.');
      },
    });
  }

  private parseFechaInicioArca(info: any, raw: any): Date | null {
    const candidatos = [
      info?.fechaInicioActividades,
      info?.inicioActividades,
      info?.activityStartDate,
      raw?.fechaInicioActividades,
      raw?.inicioActividades,
      raw?.activityStartDate,
      raw?.periodoActividadPrincipal,
    ];

    for (const valor of candidatos) {
      const fecha = this.parseFechaFlexible(valor);
      if (fecha) {
        return fecha;
      }
    }

    return null;
  }

  private parseFechaFlexible(value: unknown): Date | null {
    if (value == null) return null;
    const s = String(value).trim();
    if (!s) return null;

    // yyyyMMdd
    if (/^\d{8}$/.test(s)) {
      const y = Number(s.slice(0, 4));
      const m = Number(s.slice(4, 6));
      const d = Number(s.slice(6, 8));
      if (y > 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return new Date(y, m - 1, d);
      }
    }

    // yyyyMM (sin dia: usar día 1)
    if (/^\d{6}$/.test(s)) {
      const y = Number(s.slice(0, 4));
      const m = Number(s.slice(4, 6));
      if (y > 1900 && m >= 1 && m <= 12) {
        return new Date(y, m - 1, 1);
      }
    }

    // dd/MM/yyyy o dd-MM-yyyy
    const dmy = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (dmy) {
      const d = Number(dmy[1]);
      const m = Number(dmy[2]);
      const y = Number(dmy[3]);
      if (y > 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return new Date(y, m - 1, d);
      }
    }

    // ISO u otros parseables por Date
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}
